import { useEffect, useState } from 'react';
import AppHeader from '../components/AppHeader';
import SkillPicker from '../components/SkillPicker';
import { authApi, chaptersApi } from '../api';
import { useAuth, ROLE_LABELS } from '../contexts/MemberAuthContext';

export default function Settings() {
  const { member, refresh } = useAuth();

  // Osobní a kontaktní údaje
  const [profile, setProfile] = useState({
    first_name: member?.first_name || '', last_name: member?.last_name || '',
    email: member?.email || '', phone: member?.phone || '',
  });
  const [profileMsg, setProfileMsg] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [chapterName, setChapterName] = useState('');

  // Schopnosti
  const [skills, setSkills] = useState(member?.skills || []);
  const [skillsMsg, setSkillsMsg] = useState(null);
  const [savingSkills, setSavingSkills] = useState(false);

  // Heslo
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [msg, setMsg] = useState(null);

  // Vzhled a přístupnost (preference v localStorage, aplikované na <html>)
  const [theme, setThemeState] = useState(() => (localStorage.getItem('theme') === 'dark' ? 'dark' : 'light'));
  const [bigText, setBigText] = useState(() => localStorage.getItem('textSize') === 'large');

  const setTheme = (val) => {
    setThemeState(val);
    document.documentElement.dataset.theme = val;
    localStorage.setItem('theme', val);
  };

  const toggleBigText = () => {
    const on = !bigText;
    setBigText(on);
    if (on) { document.documentElement.dataset.textSize = 'large'; localStorage.setItem('textSize', 'large'); }
    else { delete document.documentElement.dataset.textSize; localStorage.removeItem('textSize'); }
  };

  useEffect(() => {
    if (!member?.chapter_id) { setChapterName('— bez spolku —'); return; }
    chaptersApi.list()
      .then((r) => setChapterName(r.data.find((c) => c.id === member.chapter_id)?.name || '—'))
      .catch(() => setChapterName('—'));
  }, [member?.chapter_id]);

  const setP = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setSavingProfile(true);
    try {
      await authApi.updateProfile({
        first_name: profile.first_name, last_name: profile.last_name,
        email: profile.email.trim(), phone: profile.phone,
      });
      await refresh();
      setProfileMsg({ type: 'success', text: 'Údaje byly uloženy.' });
    } catch (err) {
      setProfileMsg({ type: 'warning', text: err.response?.data?.error || 'Uložení selhalo.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const saveSkills = async () => {
    setSkillsMsg(null);
    setSavingSkills(true);
    try {
      await authApi.updateSkills(skills);
      await refresh();
      setSkillsMsg({ type: 'success', text: 'Schopnosti byly uloženy.' });
    } catch (err) {
      setSkillsMsg({ type: 'warning', text: err.response?.data?.error || 'Uložení selhalo.' });
    } finally {
      setSavingSkills(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next.length < 10) return setMsg({ type: 'warning', text: 'Nové heslo musí mít alespoň 10 znaků.' });
    if (next !== confirmPass) return setMsg({ type: 'warning', text: 'Hesla se neshodují.' });
    try {
      await authApi.changePassword(current, next);
      setMsg({ type: 'success', text: 'Heslo bylo změněno.' });
      setCurrent(''); setNext(''); setConfirmPass('');
    } catch (err) {
      setMsg({ type: 'warning', text: err.response?.data?.error || 'Změna hesla selhala.' });
    }
  };

  const initials = member
    ? (`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase() || '?')
    : '';

  return (
    <div>
      <AppHeader subtitle="Nastavení" />
      <div className="container" style={{ paddingTop: 24 }}>

        {/* Hlavička profilu */}
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div className="avatar" style={{ width: 52, height: 52, fontSize: '1.1rem' }}>{initials}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{member?.first_name} {member?.last_name}</div>
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>
              {ROLE_LABELS[member?.role] || member?.role} · {chapterName}
            </div>
          </div>
        </div>

        {/* Osobní a kontaktní údaje */}
        <div className="card">
          <h2 className="mb-16">Osobní a kontaktní údaje</h2>
          <form onSubmit={saveProfile}>
            <div className="flex gap-8">
              <div className="form-group flex-1">
                <label>Jméno</label>
                <input className="form-input" value={profile.first_name} onChange={setP('first_name')} required />
              </div>
              <div className="form-group flex-1">
                <label>Příjmení</label>
                <input className="form-input" value={profile.last_name} onChange={setP('last_name')} required />
              </div>
            </div>
            <div className="form-group">
              <label>E-mail <span className="text-muted">(slouží k přihlášení)</span></label>
              <input type="email" className="form-input" value={profile.email} onChange={setP('email')} autoComplete="email" required />
            </div>
            <div className="form-group">
              <label>Telefon <span className="text-muted">(volitelné)</span></label>
              <input className="form-input" value={profile.phone} onChange={setP('phone')} />
            </div>
            {profileMsg && <div className={`alert alert-${profileMsg.type} mb-16`}>{profileMsg.text}</div>}
            <button type="submit" className="btn btn-primary" disabled={savingProfile}>
              {savingProfile ? 'Ukládám…' : 'Uložit údaje'}
            </button>
          </form>
        </div>

        {/* Schopnosti */}
        <div className="card">
          <h2 className="mb-8">Moje schopnosti</h2>
          <p className="text-muted mb-16" style={{ fontSize: '0.88rem' }}>
            Oblasti, ve kterých máte výcvik nebo zkušenosti. Vidí je operační středisko a zástupce spolku.
          </p>
          <SkillPicker value={skills} onChange={setSkills} />
          {skillsMsg && <div className={`alert alert-${skillsMsg.type} mt-16 mb-8`}>{skillsMsg.text}</div>}
          <button className="btn btn-primary mt-16" onClick={saveSkills} disabled={savingSkills}>
            {savingSkills ? 'Ukládám…' : 'Uložit schopnosti'}
          </button>
        </div>

        {/* Vzhled */}
        <div className="card">
          <h2 className="mb-8">Vzhled</h2>
          <p className="text-muted mb-16" style={{ fontSize: '0.88rem' }}>Barevný režim aplikace.</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={`btn ${theme === 'light' ? 'btn-primary' : 'btn-outline'} flex-1`} onClick={() => setTheme('light')}>☀️ Světlý</button>
            <button className={`btn ${theme === 'dark' ? 'btn-primary' : 'btn-outline'} flex-1`} onClick={() => setTheme('dark')}>🌙 Tmavý</button>
          </div>
        </div>

        {/* Přístupnost */}
        <div className="card">
          <h2 className="mb-8">Přístupnost</h2>
          <p className="text-muted mb-16" style={{ fontSize: '0.88rem' }}>
            Větší písmo pro lepší čitelnost (např. v terénu nebo na slunci).
          </p>
          <button className={`btn ${bigText ? 'btn-primary' : 'btn-outline'} btn-full`} onClick={toggleBigText}>
            {bigText ? '✓ Větší písmo je zapnuté' : 'Zapnout větší písmo'}
          </button>
        </div>

        {/* Heslo */}
        <div className="card">
          <h2 className="mb-16">Změna hesla</h2>
          <form onSubmit={changePassword}>
            <div className="form-group">
              <label>Aktuální heslo</label>
              <input type="password" className="form-input" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
            </div>
            <div className="form-group">
              <label>Nové heslo (min. 10 znaků)</label>
              <input type="password" className="form-input" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required />
            </div>
            <div className="form-group">
              <label>Potvrzení nového hesla</label>
              <input type="password" className="form-input" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} autoComplete="new-password" required />
            </div>
            {msg && <div className={`alert alert-${msg.type} mb-16`}>{msg.text}</div>}
            <button type="submit" className="btn btn-primary btn-full">Změnit heslo</button>
          </form>
        </div>
      </div>
    </div>
  );
}
